import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Linkedin, 
  Github,
  Shield,
  ArrowUp,
  Brain,
  Lock,
  Eye,
  Zap,
  Network,
  Activity,
  Cpu,
  Database,
  Globe,
  Code,
  Terminal
} from 'lucide-react';
import cognisafeLogo from '../assets/cognisafe_logo.png';
import { memo } from 'react';
import OptimizedImage from './ui/OptimizedImage';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const footerElement = document.querySelector('#footer');
      if (footerElement) {
        const rect = footerElement.getBoundingClientRect();
        setIsVisible(rect.top < window.innerHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Digital matrix data streams
  const matrixNodes = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 5,
    speed: 0.5 + Math.random() * 1.5,
  }));

  const footerLinks = {
    product: [
      { name: 'Özellikler', href: '#features', icon: Brain },
      { name: 'Çözümler', href: '#solutions', icon: Network },
      { name: 'Fiyatlandırma', href: '#contact', icon: Database },
      { name: 'Demo', href: '#contact', icon: Eye }
    ],
    company: [
      { name: 'Hakkımızda', href: '#about', icon: Shield },
      { name: 'Kariyer', href: '#contact', icon: Activity },
      { name: 'Blog', href: '#contact', icon: Terminal },
      { name: 'Basın', href: '#contact', icon: Globe }
    ],
    support: [
      { name: 'Yardım Merkezi', href: '#contact', icon: Code },
      { name: 'Dokümantasyon', href: '#contact', icon: Database },
      { name: 'API', href: '#contact', icon: Cpu },
      { name: 'Durum', href: '#contact', icon: Activity }
    ],
    legal: [
      { name: 'Gizlilik Politikası', href: '#contact', icon: Lock },
      { name: 'Kullanım Şartları', href: '#contact', icon: Shield },
      { name: 'KVKK', href: '#contact', icon: Eye },
      { name: 'Çerezler', href: '#contact', icon: Database }
    ]
  };

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/cogni-safe', label: 'LinkedIn', color: 'from-blue-500 to-blue-600' },
  ];

  const securityFeatures = [
    { icon: Shield, text: 'Quantum Encryption' },
    { icon: Brain, text: 'AI-Powered Defense' },
    { icon: Network, text: 'Neural Networks' },
    { icon: Lock, text: 'Zero-Trust Security' },
    { icon: Eye, text: 'Behavioral Analysis' },
    { icon: Zap, text: 'Real-time Protection' }
  ];

  return (
    <footer id="footer" className="relative bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-white overflow-hidden">
      {/* Digital Matrix Background */}
      <div className="absolute inset-0 opacity-20">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Neural Network Visualization */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="w-full h-full opacity-30" viewBox="0 0 1000 800">
          <defs>
            <linearGradient id="footerDataFlow" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#06b6d4" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.4" />
            </linearGradient>
            <radialGradient id="footerNodeGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.2" />
            </radialGradient>
          </defs>
          
          {/* Data flow connections */}
          {matrixNodes.map((node, i) => 
            matrixNodes.slice(i + 1).map((targetNode, j) => {
              const distance = Math.sqrt(
                Math.pow(node.x - targetNode.x, 2) + Math.pow(node.y - targetNode.y, 2)
              );
              if (distance < 30) {
                return (
                  <motion.line
                    key={`${i}-${j}`}
                    x1={`${node.x * 10}px`}
                    y1={`${node.y * 8}px`}
                    x2={`${targetNode.x * 10}px`}
                    y2={`${targetNode.y * 8}px`}
                    stroke="url(#footerDataFlow)"
                    strokeWidth="1.5"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.5 }}
                    transition={{
                      duration: 4,
                      delay: Math.max(node.delay, targetNode.delay),
                      repeat: Infinity,
                      repeatType: "reverse",
                      ease: "easeInOut",
                    }}
                  />
                );
              }
              return null;
            })
          )}

          {/* Data nodes */}
          {matrixNodes.map((node, i) => (
            <motion.circle
              key={i}
              cx={`${node.x * 10}px`}
              cy={`${node.y * 8}px`}
              r="3"
              fill="url(#footerNodeGlow)"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ 
                scale: [1, 1.5, 1], 
                opacity: [0.4, 1, 0.4],
                y: [0, -20, 0]
              }}
              transition={{
                duration: 5,
                delay: node.delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}

          {/* Data streams */}
          {Array.from({ length: 8 }).map((_, i) => (
            <motion.path
              key={`stream-${i}`}
              d={`M ${i * 125} 0 Q ${i * 125 + 50} 400 ${i * 125 + 100} 800`}
              stroke="url(#footerDataFlow)"
              strokeWidth="2"
              fill="none"
              strokeDasharray="10 5"
              initial={{ strokeDashoffset: 0 }}
              animate={{ strokeDashoffset: -50 }}
              transition={{
                duration: 2 + i * 0.3,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          ))}
        </svg>
      </div>

      {/* Floating Security Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {securityFeatures.map((feature, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${5 + (i * 15)}%`,
              top: `${10 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [-15, 15, -15],
              rotate: [0, 3, 0, -3, 0],
              scale: [0.9, 1.1, 0.9],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 8 + i,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.4,
            }}
          >
            <div className="p-3 bg-blue-500/10 rounded-2xl backdrop-blur-sm border border-blue-300/20 group">
              <feature.icon className="w-6 h-6 text-blue-400/60" />
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-blue-400/40 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                {feature.text}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Footer Content */}
      <div className="relative z-10">
        {/* Top Section with Enhanced Branding */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Enhanced Company Info */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isVisible ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mb-8"
              >
                {/* Enhanced Logo */}
                <div className="flex items-center space-x-4 mb-6">
                  <motion.div
                    className="relative w-16 h-16 bg-gradient-to-br from-blue-500/20 to-cyan-400/20 rounded-2xl border border-blue-400/30 backdrop-blur-sm"
                    animate={{
                      boxShadow: [
                        '0 0 30px rgba(59, 130, 246, 0.3)',
                        '0 0 50px rgba(6, 182, 212, 0.4)',
                        '0 0 30px rgba(59, 130, 246, 0.3)',
                      ],
                    }}
                    transition={{ duration: 4, repeat: Infinity }}
                  >
                    <OptimizedImage 
                      src={cognisafeLogo} 
                      alt="Cognisafe - AI Destekli İnsan Odaklı Siber Güvenlik Platformu Logo" 
                      className="w-10 h-10 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 brightness-0 invert"
                      loading="lazy"
                      width={40}
                      height={40}
                    />
                    
                    {/* Neural pulse rings */}
                    {[1, 2, 3].map((ring) => (
                      <motion.div
                        key={ring}
                        className="absolute inset-0 rounded-2xl border border-blue-400/30"
                        animate={{
                          scale: [1, 1.5, 1],
                          opacity: [0.6, 0, 0.6],
                        }}
                        transition={{ 
                          duration: 3, 
                          repeat: Infinity,
                          delay: ring * 0.5
                        }}
                      />
                    ))}
                  </motion.div>
                  
                  <div className="flex flex-col">
                    <motion.span 
                      className="text-3xl font-heading font-black bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent"
                      animate={{
                        backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                      }}
                      transition={{ duration: 4, repeat: Infinity }}
                    >
                      Cognisafe
                    </motion.span>
                    <span className="text-sm text-blue-400/70 font-sans tracking-wider">CYBER INTELLIGENCE PLATFORM</span>
                  </div>
                </div>

                <p className="text-slate-300 leading-relaxed mb-8 font-sans text-lg">
                  İnsan odaklı siber güvenlik alanında Türkiye'nin öncü platformu. 
                  Yapay zeka ve davranış bilimini birleştirerek güvenliği yeniden tanımlıyoruz.
                </p>

                {/* Enhanced Contact Info */}
                <div className="space-y-4">
                  {[
                    { icon: Mail, text: 'info@cognisafe.com.tr', type: 'email' },
                    { icon: Phone, text: '+90 (530) 494 1022', type: 'phone' },
                    { icon: MapPin, text: 'İTÜ ARI Teknokent, İstanbul', type: 'location' }
                  ].map((contact, i) => (
                    <motion.div
                      key={i}
                      className="group flex items-center space-x-4 p-3 rounded-xl bg-blue-500/5 border border-blue-400/10 hover:bg-blue-500/10 hover:border-blue-400/20 transition-all duration-300"
                      whileHover={{ x: 10, scale: 1.02 }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={isVisible ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.3 + i * 0.1 }}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-cyan-400/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <contact.icon className="h-5 w-5 text-blue-400" />
                      </div>
                      <span className="text-slate-300 font-sans group-hover:text-white transition-colors">{contact.text}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Enhanced Links Grid */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {Object.entries(footerLinks).map(([category, links], categoryIndex) => {
                  const categoryTitles = {
                    product: 'Ürün',
                    company: 'Şirket', 
                    support: 'Destek',
                    legal: 'Yasal'
                  };
                  
                  return (
                    <motion.div
                      key={category}
                      initial={{ opacity: 0, y: 30 }}
                      animate={isVisible ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.2 + categoryIndex * 0.05 }}
                      className="space-y-6"
                    >
                      <h4 className="font-heading font-bold text-xl text-white mb-6 relative">
                        {categoryTitles[category]}
                        <motion.div
                          className="absolute -bottom-2 left-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400"
                          initial={{ width: 0 }}
                          animate={isVisible ? { width: '60%' } : {}}
                          transition={{ duration: 0.5, delay: 0.3 + categoryIndex * 0.05 }}
                        />
                      </h4>
                      
                      <ul className="space-y-3">
                        {links.map((link, linkIndex) => {
                          const Icon = link.icon;
                          return (
                            <li key={linkIndex}>
                              <motion.button
                                onClick={() => scrollToSection(link.href)}
                                className="group flex items-center space-x-3 text-slate-400 hover:text-white transition-all duration-300 font-sans p-2 rounded-lg hover:bg-blue-500/10"
                                whileHover={{ x: 5, scale: 1.02 }}
                                initial={{ opacity: 0, x: -10 }}
                                animate={isVisible ? { opacity: 1, x: 0 } : {}}
                                transition={{ delay: 0.7 + categoryIndex * 0.1 + linkIndex * 0.05 }}
                              >
                                <div className="w-6 h-6 bg-blue-500/20 rounded-md flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                                  <Icon className="w-3 h-3" />
                                </div>
                                <span className="group-hover:text-blue-300 transition-colors text-xs md:text-base">{link.name}</span>
                                
                                {/* Neural connection indicator */}
                                <motion.div
                                  className="w-0 h-0.5 bg-gradient-to-r from-blue-400 to-cyan-400 group-hover:w-4 transition-all duration-300 ml-auto"
                                />
                              </motion.button>
                            </li>
                          );
                        })}
                      </ul>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Newsletter Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="border-t border-blue-400/20 bg-gradient-to-r from-blue-950/50 to-slate-950/50 backdrop-blur-sm"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col lg:flex-row items-center justify-between space-y-8 lg:space-y-0">
              <div className="text-center lg:text-left">
                <h4 className="font-heading font-bold text-2xl mb-3 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Siber Güvenlik İstihbaratı
                </h4>
                <p className="text-slate-300 font-sans text-lg max-w-md">
                  En son siber tehdit analizleri, AI güvenlik trendleri ve Cognisafe güncellemeleri için abone olun.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row w-full lg:w-auto space-y-4 sm:space-y-0 sm:space-x-4">
                <div className="relative">
                  <input
                    type="email"
                    placeholder="E-posta adresiniz"
                    className="w-full sm:w-80 px-6 py-4 bg-slate-800/50 border border-blue-400/30 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 font-sans backdrop-blur-sm transition-all duration-300"
                  />
                  <motion.div
                    className="absolute inset-0 rounded-xl border border-blue-400/20 pointer-events-none"
                    animate={{
                      boxShadow: [
                        '0 0 0 rgba(59, 130, 246, 0)',
                        '0 0 20px rgba(59, 130, 246, 0.2)',
                        '0 0 0 rgba(59, 130, 246, 0)',
                      ],
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                </div>
                
                <motion.button 
                  className="group relative px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-cyan-400 hover:to-blue-500 text-white rounded-xl font-sans font-bold transition-all duration-300 overflow-hidden"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="relative z-10 flex items-center">
                    <Zap className="mr-2 w-5 h-5" />
                    Abone Ol
                  </span>
                  
                  {/* Neural pulse background */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Bottom Footer */}
        <div className="border-t border-blue-400/20 bg-slate-950/80 backdrop-blur-sm">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row items-center justify-between space-y-6 lg:space-y-0">
              <motion.div
                initial={{ opacity: 0 }}
                animate={isVisible ? { opacity: 1 } : {}}
                transition={{ duration: 0.8, delay: 1 }}
                className="text-slate-400 font-sans flex items-center space-x-4"
              >
                <span>© 2025 Cognisafe. Tüm hakları saklıdır.</span>
                <div className="hidden sm:flex items-center space-x-2 text-xs">
                  <Shield className="w-3 h-3 text-blue-400" />
                  <span className="text-blue-400">Quantum Secured</span>
                </div>
              </motion.div>

              {/* Enhanced Social Links */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={isVisible ? { opacity: 1 } : {}}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="flex items-center space-x-4"
              >
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group relative w-12 h-12 bg-gradient-to-br ${social.color} rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 overflow-hidden`}
                    whileHover={{ y: -5 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.label}
                  >
                    <social.icon className="h-5 w-5 text-white relative z-10" />
                    
                    {/* Neural pulse effect */}
                    <motion.div
                      className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0, 0.3, 0],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    
                    {/* Holographic border */}
                    <motion.div
                      className="absolute inset-0 rounded-xl border border-white/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      animate={{
                        boxShadow: [
                          '0 0 10px rgba(255, 255, 255, 0.2)',
                          '0 0 20px rgba(255, 255, 255, 0.4)',
                          '0 0 10px rgba(255, 255, 255, 0.2)',
                        ],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  </motion.a>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Scroll to Top Button */}
      <motion.button
        onClick={scrollToTop}
        className="group fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 text-white rounded-2xl shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 z-50 overflow-hidden"
        initial={{ opacity: 0, scale: 0, rotate: -180 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        whileHover={{ scale: 1.1, y: -5 }}
        whileTap={{ scale: 0.9 }}
      >
        <ArrowUp className="h-6 w-6 mx-auto relative z-10" />
        
        {/* Neural pulse background */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          animate={{
            scale: [1, 1.1, 1],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        
        {/* Holographic border effect */}
        <motion.div
          className="absolute inset-0 rounded-2xl border-2 border-blue-300/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          animate={{
            boxShadow: [
              '0 0 20px rgba(59, 130, 246, 0.3)',
              '0 0 40px rgba(6, 182, 212, 0.4)',
              '0 0 20px rgba(59, 130, 246, 0.3)',
            ],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        
        {/* Data stream indicators */}
        {[1, 2, 3].map((stream) => (
          <motion.div
            key={stream}
            className="absolute w-1 h-8 bg-gradient-to-t from-transparent to-white/30 rounded-full"
            style={{
              left: `${20 + stream * 15}%`,
              bottom: '20%',
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: stream * 0.2,
            }}
          />
        ))}
      </motion.button>
    </footer>
  );
};

export default memo(Footer);

